import { useQuery } from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from '../../api/client'
import { isThisDevice, parseDevices, type Device } from '../../api/devices'
import { keys } from '../../api/keys'
import { getDeviceId } from '../../auth/deviceId'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'

/** The backend writes the device row in a background task after the session call. */
const RETRY_EVERY_MS = 5000
const MAX_RETRIES = 3

/**
 * `GET /devices`, parsed. The first load can miss THIS browser (the row is
 * written after `POST /users/me/session` returns), so while no row is current
 * it refetches every 5s, at most 3 times after the first load, then stops.
 */
export function useDevices(): AsyncState<Device[]> {
  // Answers received, counted where they happen: `refetchInterval` is re-evaluated on
  // more than every fetch, so counting inside it would overcount.
  const fetches = useRef(0)
  const query = useQuery({
    queryKey: keys.devices.list(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/devices')
      if (error !== undefined || data === undefined) throw new Error('Could not load devices.')
      // Only answers spend the budget, so error retries and "Try again" do not.
      fetches.current += 1
      return data.devices
    },
    select: (wire) => parseDevices(wire, getDeviceId()),
    refetchInterval: (q) => {
      const data = q.state.data
      if (data === undefined || fetches.current > MAX_RETRIES) return false
      const localId = getDeviceId()
      return data.some((d) => isThisDevice(d.device_fingerprint, localId)) ? false : RETRY_EVERY_MS
    },
  })
  return fromQuery(query, {
    message: () => 'We couldn’t load your devices. Check your connection and try again.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (devices) => devices.length === 0,
  })
}
